import { useTranslation } from '../../../../../shared/i18n';
import React, { useEffect, useState } from 'react';
import { Alert, Button, Input, Radio, Space, Typography, message } from 'antd';
import { CopyOutlined, CheckCircleFilled, ThunderboltOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import domainRegistrationService from '../../../../services/domainRegistrationService';
import dnsService from '../../../../services/dnsService';
import { userPath } from '../../../../../shared/config/paths';

const { Text } = Typography;

const toList = (ns) => {
    if (Array.isArray(ns)) return ns.filter(Boolean);
    if (typeof ns === 'string' && ns) {
        try {
            const p = JSON.parse(ns);
            if (Array.isArray(p)) return p.filter(Boolean);
        } catch (_) { /* plain string */ }
        return ns.split(/[\s,]+/).filter(Boolean);
    }
    return [];
};

const norm = (l) => l.map((x) => String(x).toLowerCase().replace(/\.$/, '')).sort().join(',');

/**
 * 找到域名关联的 DNS 区域（linked_domain_id 或同名 zone）。zones 可由调用方预先传入。
 */
export const findZoneForDomain = (zones, domain) => {
    if (!domain || !Array.isArray(zones)) return null;
    const name = String(domain.domain || '').toLowerCase();
    return zones.find((z) => Number(z.linked_domain_id) === Number(domain.id))
        || zones.find((z) => String(z.zone || '').toLowerCase().replace(/\.$/, '') === name)
        || null;
};

/**
 * DNS 服务器编辑：Yeap DNS（区域分配的 NS）/ 自定义。
 * props: domain（用户域名记录）, zone（可选：已知的关联区域）, onSaved, onCancel（可选，显示取消按钮）
 */
const NameserverEditor = ({ domain, zone: zoneProp, onSaved, onCancel }) => {
    const { t } = useTranslation();
    const current = toList(domain?.nameservers);
    const [zone, setZone] = useState(zoneProp || null);
    const [yeapNs, setYeapNs] = useState([]);
    const [mode, setMode] = useState('custom');
    const [text, setText] = useState(current.join('\n'));
    const [saving, setSaving] = useState(false);
    const [switching, setSwitching] = useState(false);
    const [nowNs, setNowNs] = useState(current);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            let z = zoneProp || null;
            if (!z && domain?.domain) {
                try {
                    const r = await dnsService.getDnsZones({ search: domain.domain });
                    z = findZoneForDomain(Array.isArray(r?.data) ? r.data : [], domain);
                } catch (_) { z = null; }
            }
            if (cancelled) return;
            setZone(z);
            if (z?.id) {
                try {
                    const r = await dnsService.getDnsZone(z.id);
                    const ns = Array.isArray(r?.data?.nameservers) ? r.data.nameservers : [];
                    if (cancelled) return;
                    setYeapNs(ns);
                    if (ns.length && (current.length === 0 || norm(ns) === norm(current))) setMode('yeap');
                } catch (_) { /* zone NS unavailable */ }
            }
        };
        setText(current.join('\n'));
        setNowNs(current);
        setMode('custom');
        setYeapNs([]);
        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domain?.id, zoneProp?.id]);

    const usingYeap = yeapNs.length > 0 && norm(yeapNs) === norm(nowNs);

    const oneClick = async () => {
        setSwitching(true);
        try {
            const r = await domainRegistrationService.useYeapDns(domain.id);
            if (r.success) {
                const ns = r.data?.nameservers || [];
                setYeapNs(ns);
                setNowNs(ns);
                setMode('yeap');
                message.success(r.data?.created ? t('Free Yeap DNS activated and switch completed') : t('Switched to Yeap DNS'));
                onSaved && onSaved(ns);
            } else {
                message.error(r.error || t('Failed to switch'));
            }
        } catch (e) {
            message.error(e.error || e.message || t('Failed to switch'));
        } finally {
            setSwitching(false);
        }
    };

    const save = async () => {
        const nameservers = mode === 'yeap' ? yeapNs : text.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
        if (nameservers.length < 2) {
            message.warning(t('At least 2 DNS servers are required'));
            return;
        }
        setSaving(true);
        try {
            const r = await domainRegistrationService.updateNameservers({ id: domain.id, nameservers });
            if (r.success) {
                message.success(t('DNS servers updated'));
                setNowNs(nameservers);
                onSaved && onSaved(nameservers);
            } else {
                message.error(r.error || t('Update failed'));
            }
        } catch (e) {
            message.error(e.error || e.message || t('Update failed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {usingYeap ? (
                <Alert
                    type="success"
                    showIcon
                    icon={<CheckCircleFilled />}
                    message={t('Using Yeap DNS')}
                    description={zone?.id ? <Link to={userPath(`/dns/records/${zone.id}`)}>{t('Manage DNS records')}</Link> : null}
                />
            ) : domain?.status === 'active' && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '12px 14px', borderRadius: 10, background: 'var(--brand-soft)' }}>
                    <span>
                        <b>{t('Use Yeap DNS')}</b>
                        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                            {zone ? t('Already in your DNS records. Point it here with one click.') : t('Automatically activate free DNS and change DNS servers')}
                        </Text>
                    </span>
                    <Button type="primary" icon={<ThunderboltOutlined />} loading={switching} onClick={oneClick}>{t('Switch in one click')}</Button>
                </div>
            )}
            <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)} optionType="button" buttonStyle="solid">
                <Radio.Button value="yeap" disabled={yeapNs.length === 0}>Yeap DNS</Radio.Button>
                <Radio.Button value="custom">{t('Custom')}</Radio.Button>
            </Radio.Group>
            {mode === 'yeap' ? (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    {yeapNs.map((ns) => (
                        <Text key={ns} code copyable={{ text: ns, icon: <CopyOutlined /> }}>{ns}</Text>
                    ))}
                </Space>
            ) : (
                <Input.TextArea
                    rows={4}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={'ns1.example.com\nns2.example.com'}
                />
            )}
            {!(mode === 'yeap' && usingYeap) && (
                <Space>
                    <Button type={mode === 'custom' ? 'primary' : 'default'} loading={saving} onClick={save}>{t('Save')}</Button>
                    {onCancel && <Button onClick={onCancel}>{t('Cancel')}</Button>}
                </Space>
            )}
            {mode === 'yeap' && usingYeap && onCancel && <Button onClick={onCancel}>{t('Close')}</Button>}
        </Space>
    );
};

export default NameserverEditor;
